import { useCallback } from 'react';
import { useModal } from './useModal';
import { useDatasetFormData } from './context/useDatasetFormData';
import Modal from '../components/Modals/Modal/Modal';
import UnsavedChangesModalContent from '../components/Modals/UnsavedChangesModalContent/UnsavedChangesModalContent';

export interface UseUnsavedChangesModalReturn {
  // Guards paginator navigation: opens save/discard modal when form has unsaved changes
  navigate: (action: () => void) => void;
  // Renders null when closed; place unconditionally in JSX
  modalElement: React.ReactNode;
}

const useUnsavedChangesModal = (): UseUnsavedChangesModalReturn => {
  const { formValuesChanged, forceSave, submitDatasetForm } =
    useDatasetFormData();
  const modal = useModal();

  // Skip modal when form is clean; otherwise ask save or discard before navigating
  const navigate = useCallback(
    (action: () => void): void => {
      if (!formValuesChanged && !forceSave) {
        action();
        return;
      }
      modal.open(
        <UnsavedChangesModalContent
          onSave={() => {
            modal.close();
            submitDatasetForm(action);
          }}
          onDiscard={() => {
            modal.close();
            action();
          }}
        />
      );
    },
    [formValuesChanged, forceSave, submitDatasetForm, modal]
  );

  const modalElement = modal.isOpen ? <Modal {...modal.modalProps} /> : null;

  return { navigate, modalElement };
};

export default useUnsavedChangesModal;
